// Telemetry wiring for the app (IMPLEMENTATION §2.9 "Telemetry"): one object main.ts creates at boot —
//   telemetry  the persisted ring (telemetry.ts; the SaveService logs its save rows into it too)
//   sink       the GameController observer (telemetrySink.ts: SimEvent / command rows)
//   frame(now) rAF timestamps → `fps` rows (p50 / p5 per ui.json debug.telemetryPeriodSec of wall time;
//              extra {frames, hitches})
//   sessionStart(info)  the session_start row (device id, UA, viewport, DPR, layout class, cell); the device id
//                       is read back from the stored ring's newest session_start (a new random one on a
//                       fresh ring — it lives and dies with the ring and its session numbers)
//   layout(l)           a `layout` row whenever the class / cell size / board changes after that (an
//                       expansion, a rotation; GDD §15 `layout{class, cellPx}`): drags and rage taps are
//                       read against the cell size they happened at
//   onSave(reason)      with EVERY save (M11 lifecycle: timer / debounce / hidden / pagehide / freeze /
//                       contextlost / handover / reload): on hide (and the lock handover) the open stall /
//                       Gallery / fps totals are closed, on pagehide / handover a session_end row is added,
//                       then ONE flush — so there is at most one localStorage telemetry write per save
//   onSaveFailed(reason) a game save that failed for lack of space (not a conflict / disabled service): an
//                       `error` row (subject `save`), and the stored ring is REMOVED so the game save can use
//                       its space (the rows stay in memory; the next successful save's flush rewrites them)
//   captureErrors(win)  window `error` / `unhandledrejection` → `error` rows (≤ telemetryErrorMax per session);
//                       `captureWindowErrors` is the same listener pair for errors before the hub exists
import type { UiData } from '../data/schema.ts';
import type { SaveReason as LifecycleSaveReason } from '../game/lifecycle.ts';
import type { KeyValueStore } from '../save/storage.ts';
import { createFpsSampler, type FpsSample } from './fpsSampler.ts';
import { createTelemetry, type Telemetry } from './telemetry.ts';
import {
  errorRow,
  extraJson,
  type SessionInfo,
  sessionEndRow,
  sessionStartRow,
} from './telemetryRows.ts';
import { createTelemetrySink, type TelemetrySink } from './telemetrySink.ts';

export interface TelemetryHubDeps {
  /** ui.json debug (telemetryCap, telemetryPeriodSec, telemetryErrorMax, telemetryTextMax). */
  cfg: UiData['debug'];
  /** A frame longer than this is a hitch (ui.json render.frameClampMs). */
  hitchMs: number;
  /** Where the ring persists (localStorage). */
  store: KeyValueStore;
  /** Wall clock (Date.now). */
  now(): number;
  /** The running sim's tick (late-bound: the controller is created after the save loads). */
  tick(): number;
  /** A new device id when the stored ring has none (default: 64 random bits, hex). */
  newDeviceId?: () => string;
}

function randomId(): string {
  const a = new Uint32Array(2);
  globalThis.crypto.getRandomValues(a);
  return Array.from(a, (n) => n.toString(16).padStart(8, '0')).join('');
}

/** The `device` of the newest stored session_start, else null. */
function storedDevice(rows: readonly { event: string; extra: string }[]): string | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i];
    if (r?.event !== 'session_start') continue;
    try {
      const dev: unknown = (JSON.parse(r.extra) as { device?: unknown }).device;
      if (typeof dev === 'string' && dev.length > 0) return dev;
    } catch {
      // not JSON: keep looking
    }
  }
  return null;
}

/** The lifecycle's save reasons (game/lifecycle.ts, M11). */
export type SaveReason = LifecycleSaveReason;

/**
 * Saves that mean the page is going away: close the open totals. `handover` (M11: this instance lost the
 * single-instance lock; its last save) is the last write this ring ever gets, like pagehide.
 */
const HIDE_REASONS: readonly string[] = ['hidden', 'pagehide', 'freeze', 'handover'];
/** Saves after which this instance's session is over: a session_end row (subject = the reason). */
const END_REASONS: readonly string[] = ['pagehide', 'handover'];

export interface TelemetryHub {
  telemetry: Telemetry;
  sink: TelemetrySink;
  /** This device's id (session_start `device`). */
  device: string;
  frame(now: number): void;
  sessionStart(info: SessionInfo): void;
  layout(l: { cls: string; cellCss: number; cols: number; rows: number }): void;
  onSave(reason: SaveReason): void;
  /** The game save of `reason` failed (storage full): error row, the stored ring gives its space back. */
  onSaveFailed(reason: SaveReason): void;
  /** An `error` row, at most telemetryErrorMax per session. */
  error(source: string, message: string): void;
  captureErrors(win: Window): () => void;
}

export function createTelemetryHub(d: TelemetryHubDeps): TelemetryHub {
  const telemetry = createTelemetry({
    cap: d.cfg.telemetryCap,
    session: 0,
    now: d.now,
    tick: d.tick,
    store: d.store,
    textMax: d.cfg.telemetryTextMax,
  });
  const sink = createTelemetrySink(telemetry, { periodSec: d.cfg.telemetryPeriodSec });
  const fps = createFpsSampler({
    periodMs: d.cfg.telemetryPeriodSec * 1000,
    hitchMs: d.hitchMs,
    gapMs: d.cfg.telemetryFpsGapMs,
  });
  const device = storedDevice(telemetry.rows()) ?? (d.newDeviceId ?? randomId)();
  const bootWall = d.now();
  let errors = 0;
  let lastLayout = '';
  const logFps = (s: FpsSample | null): void => {
    if (!s) return;
    const extra = extraJson({ frames: s.frames, hitches: s.hitches });
    telemetry.log({ event: 'fps', subject: 'p50', value: s.p50, extra });
    telemetry.log({ event: 'fps', subject: 'p5', value: s.p5, extra });
  };
  const error = (source: string, message: string): void => {
    if (errors >= d.cfg.telemetryErrorMax) return;
    errors += 1;
    telemetry.log(errorRow(source, message));
  };
  return {
    telemetry,
    sink,
    device,
    frame(now) {
      logFps(fps.frame(now));
    },
    sessionStart(info) {
      telemetry.log(sessionStartRow(info, device));
      lastLayout = `${info.cls}|${Math.round(info.cell * 100) / 100}`;
    },
    layout(l) {
      const cell = Math.round(l.cellCss * 100) / 100;
      const key = `${l.cls}|${cell}`;
      if (key === lastLayout || lastLayout === '') return; // before session_start: that row has it
      lastLayout = key;
      telemetry.log({
        event: 'layout',
        subject: l.cls,
        value: cell,
        extra: extraJson({ cols: l.cols, rows: l.rows }),
      });
    },
    onSave(reason) {
      if (HIDE_REASONS.includes(reason)) {
        sink.closePeriod();
        logFps(fps.flush());
      }
      if (END_REASONS.includes(reason))
        telemetry.log(sessionEndRow(reason, (d.now() - bootWall) / 1000, d.tick()));
      telemetry.flush();
    },
    onSaveFailed(reason) {
      error('save', `${reason}: write failed (storage full or blocked); stored telemetry freed`);
      telemetry.unstore();
    },
    error,
    captureErrors: (win) => captureWindowErrors(win, error),
  };
}

/**
 * Window `error` / `unhandledrejection` → `onError(source, message)` (source `window` | `promise`); returns
 * the uninstaller. main.ts buffers the errors of the lock claim with it until the hub exists.
 */
export function captureWindowErrors(
  win: Window,
  onError: (source: string, message: string) => void,
): () => void {
  const onErr = (e: ErrorEvent): void => {
    onError('window', `${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`);
  };
  const onRej = (e: PromiseRejectionEvent): void => {
    const r: unknown = e.reason;
    onError('promise', r instanceof Error ? `${r.name}: ${r.message}` : String(r));
  };
  win.addEventListener('error', onErr);
  win.addEventListener('unhandledrejection', onRej);
  return () => {
    win.removeEventListener('error', onErr);
    win.removeEventListener('unhandledrejection', onRej);
  };
}
